import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MenuComponent } from './menu/menu.component';
import { SplashComponent } from './splash/splash.component';
import { ProductDetailsComponent } from './menu/product-details/product-details.component';
import { LanguageSheetComponent } from './menu/language-sheet/language-sheet.component';
import { environment } from '../environments/environment';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { MostOrderedItemComponent } from './menu/most-ordered-item/most-ordered-item.component';
import { MostOrderedComponent } from './menu/most-ordered/most-ordered.component';
import { FloatingCartComponent } from './components/floating-cart/floating-cart.component';
import { OrderDetailsComponent } from './components/order-details/order-details.component';
import { DeliveryFormComponent } from './components/delivery-form/delivery-form.component';
import { PaymentMethodSheetComponent } from './components/payment-method-sheet/payment-method-sheet.component';
import { TipPickerComponent } from './components/tip-picker/tip-picker.component';
import { FormsModule } from '@angular/forms';
import { AdminappComponent } from './adminapp/adminapp.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { AngularFireAnalyticsModule, DEBUG_MODE, ScreenTrackingService, UserTrackingService } from '@angular/fire/compat/analytics';
import { MenuTabBarComponent } from './components/menu-tab-bar/menu-tab-bar.component';
import { CategoryGridComponent } from './menu/category-grid/category-grid.component';
import { CategoryDetailComponent } from './menu/category-detail/category-detail.component';
import { ProductListItemComponent } from './components/product-list-item/product-list-item.component';
import { SupplementCombinationsBadgeComponent } from './components/supplement-combinations-badge.component';
import { PushPromptComponent } from './components/push-prompt/push-prompt.component';
import { PricePipe } from './pipes/price.pipe';
import { TextOverflowDirective } from './shared/text-overflow.directive';
import { AttractionIconComponent } from './attractions/components/attraction-icon.component';
import { AttractionImageComponent } from './attractions/components/attraction-image.component';
import { AttractionRatingRowComponent } from './attractions/components/attraction-rating-row.component';
import { AttractionCardComponent } from './attractions/components/attraction-card.component';
import { AttractionTileComponent } from './attractions/components/attraction-tile.component';
import { AttractionRatingBreakdownComponent } from './attractions/components/attraction-rating-breakdown.component';
import { AttractionFactRowComponent } from './attractions/components/attraction-fact-row.component';
import { AttractionErrorStateComponent } from './attractions/components/attraction-error-state.component';
import { AttractionsListComponent } from './attractions/attractions-list/attractions-list.component';
import { AttractionDetailsComponent } from './attractions/attraction-details/attraction-details.component';
import { AttractionsFabComponent } from './attractions/attractions-fab/attractions-fab.component';
import { AttractionsBannerComponent } from './attractions/attractions-banner/attractions-banner.component';
import { AttractionsMosaicBannerComponent } from './attractions/attractions-mosaic-banner/attractions-mosaic-banner.component';
import { ReelOverlayComponent } from './reel/reel-overlay.component';
import { ReelEndCardComponent } from './reel/reel-end-card.component';
import { ReelAskPanelComponent } from './reel/reel-ask-panel.component';
import { ReelMarqueeComponent } from './reel/reel-marquee.component';
import { CompactTitleDirective } from './shared/compact-title.directive';
import { EventIconComponent } from './events/components/event-icon.component';
import { EventImageComponent } from './events/components/event-image.component';
import { EventMediaComponent } from './events/components/event-media.component';
import { EventPricePillComponent } from './events/components/event-price-pill.component';
import { EventDateBadgeComponent } from './events/components/event-date-badge.component';
import { EventPromotionBadgeComponent } from './events/components/event-promotion-badge.component';
import { EventBusinessInfoComponent } from './events/components/event-business-info.component';
import { EventSkeletonComponent } from './events/components/event-skeleton.component';
import { EventCardComponent } from './events/components/event-card.component';
import { EventVerticalCardComponent } from './events/components/event-vertical-card.component';
import { EventsSegmentControlComponent } from './events/components/events-segment-control.component';
import { EventsListComponent } from './events/events-list/events-list.component';
import { EventDetailsComponent } from './events/event-details/event-details.component';
import { PromotionDetailsComponent } from './promotions/promotion-details/promotion-details.component';

@NgModule({
  declarations: [
    AppComponent,
    MenuComponent,
    SplashComponent,
    ProductDetailsComponent,
    LanguageSheetComponent,
    MostOrderedItemComponent,
    MostOrderedComponent,
    FloatingCartComponent,
    OrderDetailsComponent,
    DeliveryFormComponent,
    PaymentMethodSheetComponent,
    TipPickerComponent,
    AdminappComponent,
    MenuTabBarComponent,
    CategoryGridComponent,
    CategoryDetailComponent,
    ProductListItemComponent,
    SupplementCombinationsBadgeComponent,
    PushPromptComponent,
    PricePipe,
    TextOverflowDirective,
    AttractionIconComponent,
    AttractionImageComponent,
    AttractionRatingRowComponent,
    AttractionCardComponent,
    AttractionTileComponent,
    AttractionRatingBreakdownComponent,
    AttractionFactRowComponent,
    AttractionErrorStateComponent,
    AttractionsListComponent,
    AttractionDetailsComponent,
    AttractionsFabComponent,
    AttractionsBannerComponent,
    AttractionsMosaicBannerComponent,
    ReelOverlayComponent,
    ReelEndCardComponent,
    ReelAskPanelComponent,
    ReelMarqueeComponent,
    CompactTitleDirective,
    EventIconComponent,
    EventImageComponent,
    EventMediaComponent,
    EventPricePillComponent,
    EventDateBadgeComponent,
    EventPromotionBadgeComponent,
    EventBusinessInfoComponent,
    EventSkeletonComponent,
    EventCardComponent,
    EventVerticalCardComponent,
    EventsSegmentControlComponent,
    EventsListComponent,
    EventDetailsComponent,
    PromotionDetailsComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFirestoreModule,
    AngularFireAnalyticsModule,
    // ngx-translate 17 injects the loader's configuration rather than taking it as constructor
    // arguments, so the old `useFactory` factory is gone; prefix and suffix now arrive through
    // provideTranslateHttpLoader() in `providers` below.
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    })
  ],
  providers: [
    ScreenTrackingService,
    UserTrackingService,
    { provide: DEBUG_MODE, useValue: false },
    // Unchanged paths: GET ./assets/i18n/{lang}.json, the 15 bundles in src/assets/i18n.
    provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    // Angular 18's migration replaced HttpClientModule with this. withInterceptorsFromDi()
    // preserves the old module's behaviour of honouring DI-registered interceptors.
    provideHttpClient(withInterceptorsFromDi())
  ],
  bootstrap: [AppComponent],
  // <swiper-container> / <swiper-slide> are Swiper's web components, registered in main.ts.
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
